import '.././pages.css';
import { useLocation, useSearchParams } from 'react-router-dom';
import DateRangePicker, { computeRangeForPreset } from '../../components/Filters/DateRangePicker';
import FilterPanel from '../../components/Filters/FilterPanel';
import DailyOverview from '../../components/Cards/DailyOverviewChart';
import ExecutionTrendChart from '../../components/Cards/ExecutionTrendChart';
import LastSyncDisplay from '../../components/LastSyncDisplay';
import React, { useState, useEffect } from 'react';
import TestResultsTable from '../../components/BrokenTestsTable/BrokenTestsTable';
import useFiltersData from '../../hooks/useFiltersData';
import useLastSync from '../../hooks/useLastSync';
import EmptyCard from '../../components/Cards/EmptyCard';
import NewFailuresCard from '../../components/Cards/NewFailures';
import SuccessRateCard from '../../components/Cards/DailySuccessRate';
import usePackages from '../../hooks/usePackages';
import { formatLocalDate, parseLocalDate } from '../../utils/dateFormat';

const PLATFORM_LABELS = { null: 'All Platforms', true: 'CSQ XP', false: 'Legacy' };
const PLATFORM_VALUES = { 'All Platforms': null, 'CSQ XP': true, 'Legacy': false };

function Filters({ dateRange, datePreset, setDateRange, selectedUnit, selectedPackage, selectedPlatform, onFilterApply, lastSync, lastSyncLoading, availablePackages, packagesLoading }) {
  const filterFields = [
    {
      key: 'unit',
      label: 'Unit',
      options: ['All units', 'error_and_surveys', 'screen_insights', 'session_replay'],
      defaultValue: 'All units',
    },
    {
      key: 'pkg',
      label: 'Package',
      options: packagesLoading ? [selectedPackage] : ['All packages', ...(availablePackages || []).filter(packageName => packageName !== 'sdk')],
      defaultValue: 'All packages',
      disabled: packagesLoading,
    },
    {
      key: 'platform',
      label: 'Platform',
      options: ['All Platforms', 'CSQ XP', 'Legacy'],
      toLabel: (v) => PLATFORM_LABELS[v],
      toValue: (l) => PLATFORM_VALUES[l],
      defaultValue: null,
    },
  ];

  return (
    <div className="filters-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <DateRangePicker
          value={dateRange}
          preset={datePreset}
          onChange={setDateRange}
          maxDate={new Date()}
        />
        <FilterPanel
          fields={filterFields}
          values={{ unit: selectedUnit, pkg: selectedPackage, platform: selectedPlatform }}
          onApply={onFilterApply}
        />
      </div>
      <LastSyncDisplay lastSync={lastSync} loading={lastSyncLoading} />
    </div>
  );
}

function TestsMonitoring() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => {
    const preset = searchParams.get('preset') || '7d';

    let dateRange;
    let finalPreset = preset;
    if (preset === 'custom') {
      const urlStart = searchParams.get('startDate');
      const urlEnd = searchParams.get('endDate');
      const parsedStart = parseLocalDate(urlStart);
      const parsedEnd = parseLocalDate(urlEnd);
      if (parsedStart && parsedEnd && !isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
        dateRange = {
          startDate: parsedStart,
          endDate: parsedEnd,
        };
      } else {
        dateRange = computeRangeForPreset('7d');
        finalPreset = '7d';
      }
    } else {
      dateRange = computeRangeForPreset(preset);
    }

    const selectedUnit = searchParams.get('unit') || 'All units';
    const packageParam = searchParams.get('pkg') || 'All packages';
    const selectedPackage = packageParam === 'sdk' ? 'All packages' : packageParam;

    const platformParam = searchParams.get('platform');
    let selectedPlatform = null;
    if (platformParam === 'true') {
      selectedPlatform = true;
    } else if (platformParam === 'false') {
      selectedPlatform = false;
    }

    return {
      dateRange,
      datePreset: finalPreset,
      selectedUnit,
      selectedPackage,
      selectedPlatform
    };
  });

  const location = useLocation();

  useEffect(() => {
    const params = {};
    if (filters.datePreset) {
      params.preset = filters.datePreset;
    }
    if (filters.datePreset === 'custom' && filters.dateRange.startDate && filters.dateRange.endDate) {
      params.startDate = formatLocalDate(filters.dateRange.startDate);
      params.endDate = formatLocalDate(filters.dateRange.endDate);
    } else {
      params.startDate = null;
      params.endDate = null;
    }
    if (filters.selectedUnit) {
      params.unit = filters.selectedUnit;
    }
    if (filters.selectedPackage) {
      params.pkg = filters.selectedPackage;
    }
    if (filters.selectedPlatform !== null) {
      params.platform = String(filters.selectedPlatform);
    } else {
      params.platform = null;
    }

    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(params).forEach(([k, v]) => {
        if (v == null) next.delete(k);
        else next.set(k, v);
      });
      return next;
    }, { replace: true });
  }, [filters, setSearchParams]);

  const setDateRange = ({ startDate, endDate, preset }) =>
    setFilters(prev => ({ ...prev, dateRange: { startDate, endDate }, datePreset: preset }));
  const handleFilterApply = ({ unit, pkg, platform }) =>
    setFilters(prev => ({ ...prev, selectedUnit: unit, selectedPackage: pkg, selectedPlatform: platform }));

  const startDate = formatLocalDate(filters.dateRange.startDate);
  const endDate = formatLocalDate(filters.dateRange.endDate);
  const apiPackage = filters.selectedPackage === 'All packages' ? 'sdk' : filters.selectedPackage;

  const { data, rawData, loading, error } = useFiltersData(
    startDate,
    endDate,
    'production',
    apiPackage,
    'pytest',
    filters.selectedUnit,
    filters.selectedPlatform
  );

  const { data: lastSync, loading: lastSyncLoading }  = useLastSync(
    'production', 
    apiPackage
  );

  const { data: availablePackages, loading: packagesLoading } = usePackages("sdk");

  return (
    <div className="application-container table-owns-scroll">
      {location.pathname === '/mobile/tests-monitoring' && (
        <>
          <Filters
            dateRange={filters.dateRange}
            datePreset={filters.datePreset}
            setDateRange={setDateRange}
            selectedUnit={filters.selectedUnit}
            selectedPackage={filters.selectedPackage}
            selectedPlatform={filters.selectedPlatform}
            onFilterApply={handleFilterApply}
            lastSync={lastSync}
            lastSyncLoading={lastSyncLoading}
            availablePackages={availablePackages}
            packagesLoading={packagesLoading}
          />
          <div className="flex-container">
            <div className="cards-container">
              {(filters.datePreset === '7d' || filters.datePreset === '14d') ? (
                <ExecutionTrendChart
                  rawData={rawData}
                  loading={loading}
                  error={error}
                />
              ) : (
                <DailyOverview
                  selectedDate={filters.dateRange.endDate}
                  selectedEnvironment='production'
                  selectedPackage={filters.selectedPackage}
                  selectedUnit={filters.selectedUnit}
                  selectedFramework={'pytest'}
                  data={data}
                  loading={loading}
                  error={error}
                />
              )}
              <SuccessRateCard
                data={data}
                loading={loading}
                error={error}
              />
              <NewFailuresCard
                selectedDate={filters.dateRange.endDate}
                selectedEnvironment='production'
                selectedPackage={apiPackage}
                selectedFramework='pytest'
                selectedIsCsqXp={filters.selectedPlatform}
              />
              <EmptyCard/>
            </div>
          </div>
          <TestResultsTable
            selectedDate={filters.dateRange.endDate}
            selectedStartDate={filters.dateRange.startDate}
            selectedEndDate={filters.dateRange.endDate}
            datePreset={filters.datePreset}
            selectedEnvironment='production'
            selectedPackage={apiPackage}
            selectedFramework={'pytest'}
            selectedUnit={filters.selectedUnit}
            selectedIsCsqXp={filters.selectedPlatform}
            csqXpFalseLabel='Legacy'
          />
        </>
      )}
    </div>
  );
}

export default TestsMonitoring; 